import React, { createContext, useContext, useState, useCallback } from 'react';

// Generic connection labels only (no partner names exposed in UI)
const CONNECTION_LABELS = [
  'Connection 1', 'Connection 2', 'Connection 3', 'Connection 4', 'Connection 5',
  'Connection 6', 'Connection 7', 'Connection 8', 'Connection 9', 'Connection 10',
  'Connection 11', 'Connection 12', 'Connection 13', 'Connection 14', 'Connection 15',
  'Connection 16', 'Connection 17', 'Connection 18', 'Connection 19', 'Connection 20',
];

const initialPartners = [
  { id: 1, name: CONNECTION_LABELS[0], code: 'A1B2C3', apiKey: '*******1001', clientId: 'cli_***01', clientSecret: 'sec_***01', corridorFrom: 'USA', corridorTo: 'India', status: 'Approved', dateApproved: '2024-01-15T14:30:00', dateActivated: null },
  { id: 2, name: CONNECTION_LABELS[1], code: 'X9Y8Z7', apiKey: '*******1002', clientId: 'cli_***02', clientSecret: 'sec_***02', corridorFrom: 'USA', corridorTo: 'India', status: 'Active', dateApproved: '2024-01-10T09:15:00', dateActivated: '2024-01-20T11:45:00' },
  { id: 3, name: CONNECTION_LABELS[2], code: 'M4N5P6', apiKey: '*******1003', clientId: 'cli_***03', clientSecret: 'sec_***03', corridorFrom: 'UK', corridorTo: 'India', status: 'Active', dateApproved: '2024-01-08T16:00:00', dateActivated: '2024-01-18T10:20:00' },
];

const makeLog = (log, partnerName) => ({ ...log, partner: partnerName });

const initialLogsFirst = [
  { id: 'LOG001', timestamp: '2026-01-14 10:30:00', date: '2026-01-14', action: 'Payment Created', status: 'Success', details: 'Payment ID: PAY001, Amount: $1,500', amount: 1500 },
  { id: 'LOG003', timestamp: '2026-01-14 10:20:00', date: '2026-01-14', action: 'KYC Verification', status: 'Pending', details: 'Customer ID: CUST002, Status: Under Review', amount: 0 },
  { id: 'LOG005', timestamp: '2026-01-14 10:10:00', date: '2026-01-14', action: 'Payment Processed', status: 'Success', details: 'Payment ID: PAY002, Amount: $2,300', amount: 2300 },
  { id: 'LOG007', timestamp: '2026-01-13 09:45:00', date: '2026-01-13', action: 'Customer Onboarded', status: 'Success', details: 'Customer ID: CUST003, Name: Sarah Johnson', amount: 0 },
  { id: 'LOG009', timestamp: '2026-01-13 09:30:00', date: '2026-01-13', action: 'Transaction Initiated', status: 'Pending', details: 'Transaction ID: TXN456, Amount: $3,500', amount: 3500 },
  { id: 'LOG011', timestamp: '2026-01-12 15:20:00', date: '2026-01-12', action: 'Payment Completed', status: 'Success', details: 'Payment ID: PAY004, Amount: $1,200', amount: 1200 },
];

const initialLogsSecond = [
  { id: 'LOG002', timestamp: '2026-01-14 10:25:00', date: '2026-01-14', action: 'Customer Created', status: 'Success', details: 'Customer ID: CUST001, Name: John Doe', amount: 0 },
  { id: 'LOG004', timestamp: '2026-01-14 10:15:00', date: '2026-01-14', action: 'Compliance Check', status: 'Failed', details: 'Transaction ID: TXN123, Reason: Sanctions Match', amount: 0 },
  { id: 'LOG006', timestamp: '2026-01-14 10:05:00', date: '2026-01-14', action: 'Payment Approved', status: 'Success', details: 'Payment ID: PAY003, Amount: $4,200', amount: 4200 },
  { id: 'LOG008', timestamp: '2026-01-13 09:50:00', date: '2026-01-13', action: 'KYC Submitted', status: 'Pending', details: 'Customer ID: CUST004, Documents: 3 uploaded', amount: 0 },
  { id: 'LOG010', timestamp: '2026-01-13 09:20:00', date: '2026-01-13', action: 'Refund Processed', status: 'Success', details: 'Refund ID: REF001, Amount: $850', amount: 850 },
  { id: 'LOG012', timestamp: '2026-01-12 14:30:00', date: '2026-01-12', action: 'Transaction Failed', status: 'Failed', details: 'Transaction ID: TXN789, Amount: $2,100', amount: 2100 },
];

const name0 = initialPartners[0].name;
const name1 = initialPartners[1].name;
const name2 = initialPartners[2].name;

const initialLogsByPartner = {
  [name0]: initialLogsFirst.map((l) => makeLog(l, name0)),
  [name1]: initialLogsSecond.map((l) => makeLog(l, name1)),
  [name2]: [],
};

const PartnersContext = createContext(null);

export const usePartners = () => {
  const ctx = useContext(PartnersContext);
  if (!ctx) throw new Error('usePartners must be used within PartnersProvider');
  return ctx;
};

export function PartnersProvider({ children }) {
  const [partners, setPartners] = useState(initialPartners);
  const [logsByPartner, setLogsByPartner] = useState(initialLogsByPartner);

  const addPartnerWithLogs = useCallback((newPartner) => {
    setPartners((prev) => [...prev, newPartner]);
    setLogsByPartner((prev) => ({ ...prev, [newPartner.name]: [] }));
  }, []);

  const addLogForPartner = useCallback((partnerName, logEntry) => {
    const ts = new Date();
    const date = ts.toISOString().slice(0, 10);
    const timestamp = `${date} ${ts.toTimeString().slice(0, 8)}`;
    const log = { id: `LOG${Date.now()}`, timestamp, date, ...logEntry };
    setLogsByPartner((prev) => ({
      ...prev,
      [partnerName]: [makeLog(log, partnerName), ...(prev[partnerName] || [])],
    }));
  }, []);

  return (
    <PartnersContext.Provider
      value={{
        partners,
        setPartners,
        logsByPartner,
        setLogsByPartner,
        addPartnerWithLogs,
        addLogForPartner,
        PARTNER_NAMES: CONNECTION_LABELS,
      }}
    >
      {children}
    </PartnersContext.Provider>
  );
}
